// Every date and time on the Colorist Foundations pages renders in the viewer's own local
// time plus PST, client-side, with the detected timezone named so a wrong detection is
// catchable. UTC was considered and rejected: almost nobody knows their own UTC offset.
// Source: KaurCourse design record 11.65.

// displayAt  = the instant shown to the reader, in PT and in their own zone.
// boundaryAt = the instant state logic compares against. A deadline written "23:59"
//              means you have until the end of that minute, so the boundary is 00:00.
//              Only deadline events have one.
export const CF_EVENTS = {
  // Cohort 2. Times are PST (-08:00): US daylight time ended Sun Nov 1, 2026, before every
  // date below, so none of them is -07:00 except mentor-expiry (June 2027, PDT).
  // Early Bird (Jason, 2026-10-29): 20% off every seat, ends Sun Nov 1, 2026, 23:59 PT.
  'early-bird-end':      { displayAt: '2026-11-01T23:59:00-08:00', boundaryAt: '2026-11-02T00:00:00-08:00' },
  // Deliberately the same instant as enrolment-close: refunds run until enrollment closes, so
  // nobody can buy a seat that was never refundable. If enrolment-close moves, move this with it.
  'refund-cutoff':       { displayAt: '2026-11-08T23:59:00-08:00', boundaryAt: '2026-11-09T00:00:00-08:00' },
  'enrolment-close':     { displayAt: '2026-11-08T23:59:00-08:00', boundaryAt: '2026-11-09T00:00:00-08:00' },
  'cold-grade-due':      { displayAt: '2026-11-13T23:59:00-08:00', boundaryAt: '2026-11-14T00:00:00-08:00' },
  'course-start':        { displayAt: '2026-11-15T09:00:00-08:00' },
  // Day 1's end, used time-only to state the four-hour session length. Without it a
  // reader east of PT cannot tell whether the hours are an evening or an all-nighter
  // until after they have signed up.
  'course-day-end':      { displayAt: '2026-11-15T13:00:00-08:00' },
  // Days 2 to 5. Same 09:00 to 13:00 PT session; each has its own window in CF_WINDOWS.
  'day-2':               { displayAt: '2026-11-21T09:00:00-08:00' },
  'day-2-end':           { displayAt: '2026-11-21T13:00:00-08:00' },
  'day-3':               { displayAt: '2026-11-29T09:00:00-08:00' },
  'day-3-end':           { displayAt: '2026-11-29T13:00:00-08:00' },
  'day-4':               { displayAt: '2026-12-06T09:00:00-08:00' },
  'day-4-end':           { displayAt: '2026-12-06T13:00:00-08:00' },
  'regrade-due':         { displayAt: '2026-12-09T23:59:00-08:00', boundaryAt: '2026-12-10T00:00:00-08:00' },
  'day-5':               { displayAt: '2026-12-13T09:00:00-08:00' },
  'day-5-end':           { displayAt: '2026-12-13T13:00:00-08:00' },
  // June is daylight time, so this one is genuinely PDT. Not a typo. Date-only on the page.
  'mentor-expiry':       { displayAt: '2027-06-13T23:59:00-07:00' },
};

// Countdown headings live here, next to the dates they describe, rather than in the Liquid
// `case` statement they used to sit in. In `auto` mode the winning event is resolved in the
// browser, so the heading that names it has to be resolvable in the browser too.
export const CF_EVENT_HEADINGS = {
  'early-bird-end': 'Early bird pricing ends in',
  'refund-cutoff': 'Refunds close in',
  'enrolment-close': 'Enrollment closes in',
  'cold-grade-due': 'Your cold grade is due in',
  'course-start': 'Day 1 starts in',
  'regrade-due': 'Your re-grade is due in',
  'mentor-expiry': 'Mentor sessions expire in',
};

// A window is a recurring daily span, not a single instant, so it is expressed as a pair
// of CF_EVENTS keys (the same day's start and end) rather than its own displayAt. Reusing
// course-start / course-day-end keeps the single-instant events as the one source of truth
// for the clock times; this map only says which two of them bound the window.
export const CF_WINDOWS = {
  'course-day': { start: 'course-start', end: 'course-day-end' },
  'course-day-2': { start: 'day-2', end: 'day-2-end' },
  'course-day-3': { start: 'day-3', end: 'day-3-end' },
  'course-day-4': { start: 'day-4', end: 'day-4-end' },
  'course-day-5': { start: 'day-5', end: 'day-5-end' },
};

// `auto` runs on the public funnel page, where the audience is a stranger still deciding
// whether to buy, not an enrolled participant. Only these three events are things a
// prospective buyer should be counting down to; the participant-facing ones (refund-cutoff,
// cold-grade-due, regrade-due, mentor-expiry) would be meaningless or actively wrong to show
// someone who hasn't bought yet. Must stay in chronological order.
export const CF_AUTO_LADDER = ['early-bird-end', 'enrolment-close', 'course-start'];

// How close an auto-ladder event has to be before the countdown is willing to show it.
// A key absent from this map is eligible the moment the event before it passes, which is
// the original behaviour and stays the default.
//
// enrolment-close sits a week behind early-bird-end. A clock left running that whole
// span stops reading as a deadline and starts reading as decoration, which is the exact
// countdown theatre the funnel design record rules out. So it stays dark until the last
// stretch, then comes back when the number is short enough to mean something.
export const CF_AUTO_LEAD_HOURS = {
  'enrolment-close': 72,
};

const PT_ZONE = 'America/Los_Angeles';

// 12-hour for the US, 24-hour for Europe, and the browser's own habit everywhere else. The zone
// decides rather than the browser language, because plenty of European colorists run en-US
// browsers and would otherwise get AM/PM. Jason, 2026-10-31.
const US_ZONE = /^(America\/(New_York|Detroit|Kentucky\/.+|Indiana\/.+|Chicago|Menominee|North_Dakota\/.+|Denver|Boise|Phoenix|Los_Angeles|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak|Puerto_Rico)|Pacific\/Honolulu|US\/.+)$/;

export function uses12Hour(zone, locale) {
  if (US_ZONE.test(zone)) return true;
  if (/^(Europe|Atlantic)\//.test(zone)) return false;
  try {
    return Boolean(new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12);
  } catch {
    return false;
  }
}

const DATE_ONLY_PARTS = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/**
 * American order ("Sun Nov 15, 2026") goes with the 12-hour clock and day-first order
 * ("Sun 15 Nov 2026") with the 24-hour one, so uses12Hour() decides both.
 *
 * en-GB abbreviates September to "Sept", the only four-letter month it produces, and it
 * would land directly beside "Aug" and "Nov" in the dates table. Normalize to three letters.
 */
function renderDate(iso, timeZone, withYear = true, american = true) {
  const parts = new Intl.DateTimeFormat(american ? 'en-US' : 'en-GB', { ...DATE_ONLY_PARTS, timeZone })
    .formatToParts(new Date(iso))
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  if (american) {
    const date = `${parts.weekday} ${parts.month} ${parts.day}`;
    return withYear ? `${date}, ${parts.year}` : date;
  }
  const date = `${parts.weekday} ${parts.day} ${parts.month === 'Sept' ? 'Sep' : parts.month}`;
  return withYear ? `${date} ${parts.year}` : date;
}

/**
 * Clock time alone, no date. Used when the date would be noise: either the caller
 * asked for a time-only render, or both zones land on the same calendar day and
 * printing the date twice for one moment reads as an error.
 */
export function formatTimeOnly(iso, timeZone, hour12 = false) {
  const date = new Date(iso);
  if (hour12) {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone })
      .format(date)
      .replace(/\s/g, ' ');
  }
  const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone })
    .formatToParts(date)
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.hour}:${parts.minute}`;
}

/**
 * The zone's short name on that date, so it changes with daylight time: PST, EST, CET, GMT.
 * Each English locale only knows its own abbreviations, so try both. Zones with no common
 * abbreviation come back as an offset (GMT+9), which is still correct.
 */
export function zoneLabel(iso, timeZone) {
  const label = (locale) => new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
    .formatToParts(new Date(iso))
    .find((p) => p.type === 'timeZoneName')?.value;
  const american = label('en-US');
  const name = american && !american.startsWith('GMT') ? american : label('en-GB');
  return name === 'GMT+0' ? 'GMT' : name;
}

/** Format an instant in Pacific Time, labelled PST or PDT, always 12-hour. */
export function formatPT(iso) {
  return `${renderDate(iso, PT_ZONE)}, ${formatTimeOnly(iso, PT_ZONE, true)} ${zoneLabel(iso, PT_ZONE)}`;
}

/** Format an instant in the viewer's zone, labelled. */
export function formatLocal(iso, timeZone, hour12 = false) {
  return `${renderDate(iso, timeZone, true, hour12)}, ${formatTimeOnly(iso, timeZone, hour12)} ${zoneLabel(iso, timeZone)}`;
}

/**
 * Format a recurring daily window (e.g. the four-hour live session) as a clock-time
 * range in the given zone, e.g. "19:00 to 23:00". No date, no zone label: callers add
 * both, same as formatTimeOnly.
 */
export function formatDailyWindow(startIso, endIso, timeZone, hour12 = false) {
  return `${formatTimeOnly(startIso, timeZone, hour12)} to ${formatTimeOnly(endIso, timeZone, hour12)}`;
}

/**
 * A session in the viewer's zone. A session can start on the next calendar day, or run past
 * midnight, and a clock range alone hides both. `dated` is for the dates table, where the
 * row's date has to be the viewer's own too.
 */
export function formatSession(startIso, endIso, timeZone, hour12 = false, dated = false) {
  const range = `${formatDailyWindow(startIso, endIso, timeZone, hour12)} ${zoneLabel(startIso, timeZone)}`;
  const startDay = renderDate(startIso, timeZone, true, hour12);
  const startsNextDay = startDay !== renderDate(startIso, PT_ZONE, true, hour12);
  const endsNextDay = renderDate(endIso, timeZone, true, hour12) !== startDay;
  const ending = endsNextDay ? ', ending the next day' : '';
  if (dated) return `${startDay}, ${range}${ending}`;
  return `${range}${startsNextDay ? ', the next day' : ending}`;
}

/**
 * Format an instant as a bare date, in Pacific Time, with no time-of-day and no
 * zone label. For events with no stated clock time (mentor-expiry), rendering a
 * time at all would fabricate a precision the source facts do not carry. Always
 * PT, the same reference zone the instant is stored in, so the date shown does
 * not depend on the machine or viewer's own timezone.
 */
export function formatDateOnly(iso, american = true) {
  return renderDate(iso, PT_ZONE, true, american);
}

/**
 * Describe the calendar-day distance to an event in the viewer's zone.
 * The instant decides whether the event has passed; calendar dates decide
 * today, tomorrow and the number of days so DST cannot skew the label.
 */
export function dayDifference(eventIso, nowIso, timeZone) {
  if (Date.parse(eventIso) < Date.parse(nowIso)) return 'Passed';

  const dateParts = (iso) => new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date(iso)).reduce((parts, part) => {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
    return parts;
  }, {});

  const event = dateParts(eventIso);
  const now = dateParts(nowIso);
  const eventDay = Date.UTC(event.year, event.month - 1, event.day);
  const nowDay = Date.UTC(now.year, now.month - 1, now.day);
  const days = Math.round((eventDay - nowDay) / 86400000);

  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/** Which pricing and enrolment state the page is in. */
export function salesState(nowIso) {
  const now = Date.parse(nowIso);
  if (now >= Date.parse(CF_EVENTS['enrolment-close'].boundaryAt)) return 'closed';
  if (now >= Date.parse(CF_EVENTS['early-bird-end'].boundaryAt)) return 'regular';
  return 'early-bird';
}

/**
 * Time left until an event, or null once it has passed.
 *
 * Counts to `boundaryAt` where there is one, NOT to `displayAt`. That is the same instant
 * salesState() switches on, so the digits can never reach zero while the page still says
 * early bird is live. Counting to displayAt would leave a stale minute between them.
 *
 * @param {string} key a CF_EVENTS key
 * @param {string} nowIso
 * @returns {{days:number,hours:number,minutes:number,seconds:number}|null}
 */
export function countdownParts(key, nowIso = new Date().toISOString()) {
  const event = CF_EVENTS[key];
  if (!event) return null;

  const remainingMs = Date.parse(event.boundaryAt || event.displayAt) - Date.parse(nowIso);
  if (!(remainingMs > 0)) return null;

  const totalSeconds = Math.floor(remainingMs / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor(totalSeconds / 3600) % 24,
    minutes: Math.floor(totalSeconds / 60) % 60,
    seconds: totalSeconds % 60,
  };
}

/**
 * The key of the soonest event still ahead, or null once every one has passed.
 * Ties are impossible in CF_EVENTS, but if one ever appears the earlier key in
 * declaration order wins, which keeps this deterministic.
 */
export function nextEvent(nowIso = new Date().toISOString(), keys = Object.keys(CF_EVENTS)) {
  const now = Date.parse(nowIso);
  for (const key of keys) {
    const event = CF_EVENTS[key];
    if (!event) continue;
    const target = Date.parse(event.boundaryAt || event.displayAt);
    if (target > now) return key;
  }
  return null;
}

/**
 * The auto countdown's target, or null when nothing is close enough to be worth a clock.
 *
 * Returns null rather than falling through to the next rung: if the soonest event ahead is
 * too far out to feel urgent, everything behind it is further still, so a fall-through
 * would only ever swap one piece of wallpaper for a worse one.
 *
 * @param {string} nowIso
 * @returns {string|null} a CF_EVENTS key
 */
export function autoEvent(nowIso = new Date().toISOString()) {
  const key = nextEvent(nowIso, CF_AUTO_LADDER);
  if (!key) return null;

  const leadHours = CF_AUTO_LEAD_HOURS[key];
  if (leadHours === undefined) return key;

  const event = CF_EVENTS[key];
  const target = Date.parse(event.boundaryAt || event.displayAt);
  const opensAt = target - leadHours * 3600 * 1000;
  return Date.parse(nowIso) >= opensAt ? key : null;
}

/**
 * True for zones on Pacific clocks at every date on the page, such as Tijuana, where a
 * "local" time and a Pacific time would be the same words printed twice.
 */
function followsPacific(zone) {
  if (zone === PT_ZONE) return true;
  return Object.values(CF_EVENTS).every((event) => (
    formatTimeOnly(event.displayAt, zone) === formatTimeOnly(event.displayAt, PT_ZONE)
    && zoneLabel(event.displayAt, zone) === zoneLabel(event.displayAt, PT_ZONE)
  ));
}

/**
 * The viewer's IANA zone, or PT if the browser will not say or says something unusable.
 * Some browsers report "Etc/Unknown", and handing that to Intl throws, so the name is
 * tried before anything relies on it.
 */
export function detectZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return PT_ZONE;
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return PT_ZONE;
  }
}

/**
 * Name the detected zone and show its current time, so a wrong detection is catchable
 * by the reader instead of silently misleading them.
 *
 * Separate from hydrate() and repainted on a timer because this is the one string on
 * the page that goes stale on its own: hydrate() runs once on DOMContentLoaded, so a
 * tab left open for an hour used to claim a current time an hour out of date. That
 * undermines the only line whose whole job is to let someone catch a bad detection.
 */
export function paintZoneLines(root = document, nowIso = new Date().toISOString(), zone = detectZone()) {
  const isPT = zone === PT_ZONE;
  const city = zone.split('/').pop().replace(/_/g, ' ');
  // Browsers still report these retired names, which read as a wrong detection.
  const place = { Calcutta: 'Kolkata', Kiev: 'Kyiv', Saigon: 'Ho Chi Minh City', Rangoon: 'Yangon' }[city] || city;
  root.querySelectorAll('[data-cf-zone]').forEach((el) => {
    el.textContent = isPT
      ? 'Times shown in Pacific Time.'
      : `Times shown for ${place}, where it is now ${formatTimeOnly(nowIso, zone, uses12Hour(zone))}.`;
  });
}

/**
 * Keep the zone line's clock honest. Minute resolution, because the line prints HH:MM
 * and a one-second tick would be 60x the work for no visible difference.
 *
 * Deliberately independent of startCountdowns(): that returns early on any page with no
 * countdown, and the zone line appears on pages that have none.
 *
 * @returns {number|null} the interval id, or null when the page has no zone line
 */
export function startZoneClock(root = document) {
  if (root.querySelectorAll('[data-cf-zone]').length === 0) return null;
  return setInterval(() => paintZoneLines(root), 60000);
}

/**
 * Fill every [data-cf-event] element and set the page's sales state.
 * Elements ship with a server-rendered PT string as their initial text, so the page
 * is correct and complete before this runs and if it never runs at all.
 */
export function hydrate(
  root = document,
  nowIso = new Date().toISOString(),
  zone = detectZone(),
) {
  const isPT = followsPacific(zone);
  const hour12 = uses12Hour(zone);

  root.querySelectorAll('[data-cf-event]').forEach((el) => {
    const event = CF_EVENTS[el.dataset.cfEvent];
    if (!event) return;
    el.setAttribute('datetime', event.displayAt);

    // A date-only element (mentor-expiry) has no stated clock time, so it never
    // gets the PT-plus-local-time treatment: a local-time suffix would be
    // meaningless without a time to convert, and a bare date needs no zone label.
    if (el.hasAttribute('data-cf-date-only')) {
      el.textContent = formatDateOnly(event.displayAt, hour12);
      return;
    }

    // A live session's date, which IS the viewer's own: Day 1 is a Monday in Tokyo.
    if (el.hasAttribute('data-cf-local-date')) {
      el.textContent = renderDate(event.displayAt, zone, !el.hasAttribute('data-cf-no-year'), hour12);
      return;
    }

    // data-cf-time-only: the surrounding sentence already carries the date, so
    // repeating it here just makes the line longer.
    const timeOnly = el.hasAttribute('data-cf-time-only');

    el.textContent = '';
    const primary = el.ownerDocument.createElement('span');
    primary.className = 'cf-time__local';

    if (isPT) {
      primary.textContent = timeOnly
        ? `${formatTimeOnly(event.displayAt, PT_ZONE, true)} ${zoneLabel(event.displayAt, PT_ZONE)}`
        : formatPT(event.displayAt);
      el.appendChild(primary);
      return;
    }

    primary.textContent = timeOnly
      ? `${formatTimeOnly(event.displayAt, zone, hour12)} ${zoneLabel(event.displayAt, zone)}`
      : formatLocal(event.displayAt, zone, hour12);
    el.appendChild(primary);

    // Same calendar day in both zones? The PT date is then noise, so show the clock
    // only. Different day? Print the whole PT date. The Early Bird deadline is that
    // case, and dropping its date would put the wrong day in front of most of Europe,
    // Africa and Asia.
    const sameDay = renderDate(event.displayAt, zone) === renderDate(event.displayAt, PT_ZONE);

    const pt = el.ownerDocument.createElement('span');
    pt.className = 'cf-time__pt';
    pt.textContent = timeOnly || sameDay
      ? `${formatTimeOnly(event.displayAt, PT_ZONE, true)} ${zoneLabel(event.displayAt, PT_ZONE)}`
      : formatPT(event.displayAt);

    // Explicit separator: the two spans used to be appended back to back with no
    // rule and no text node between them, so they rendered run together.
    el.appendChild(el.ownerDocument.createTextNode(' · '));
    el.appendChild(pt);
  });

  // Same PT-plus-local treatment as [data-cf-event], but for a recurring daily window
  // (start/end clock times) instead of a single instant. Mirrors that pass's structure
  // deliberately, span classes and separator included, so the existing CSS applies unchanged.
  root.querySelectorAll('[data-cf-window]').forEach((el) => {
    const windowDef = CF_WINDOWS[el.dataset.cfWindow];
    if (!windowDef) return;
    const startEvent = CF_EVENTS[windowDef.start];
    const endEvent = CF_EVENTS[windowDef.end];
    if (!startEvent || !endEvent) return;
    el.setAttribute('datetime', startEvent.displayAt);

    el.textContent = '';
    const primary = el.ownerDocument.createElement('span');
    primary.className = 'cf-time__local';

    const dated = el.hasAttribute('data-cf-dated');
    const pacific = formatSession(startEvent.displayAt, endEvent.displayAt, PT_ZONE, true, dated);

    if (isPT) {
      primary.textContent = pacific;
      el.appendChild(primary);
      return;
    }

    primary.textContent = formatSession(startEvent.displayAt, endEvent.displayAt, zone, hour12, dated);
    el.appendChild(primary);

    const pt = el.ownerDocument.createElement('span');
    pt.className = 'cf-time__pt';
    // Same rule as the events above: repeat the Pacific date only when it differs.
    const sameDay = renderDate(startEvent.displayAt, zone) === renderDate(startEvent.displayAt, PT_ZONE);
    pt.textContent = sameDay
      ? formatSession(startEvent.displayAt, endEvent.displayAt, PT_ZONE, true)
      : pacific;

    el.appendChild(el.ownerDocument.createTextNode(' · '));
    el.appendChild(pt);
  });

  root.querySelectorAll('[data-cf-days]').forEach((el) => {
    const event = CF_EVENTS[el.dataset.cfDays];
    if (!event) return;
    const label = dayDifference(event.displayAt, nowIso, zone);
    el.textContent = label;
    const row = el.closest('tr');
    if (row) row.classList.toggle('cf-date-row--passed', label === 'Passed');
  });

  paintZoneLines(root, nowIso, zone);

  root.querySelectorAll('[data-cf-state]').forEach((el) => {
    el.hidden = el.dataset.cfState !== salesState(nowIso);
  });
}

/**
 * Paint one countdown section, or hide it once its event has passed.
 *
 * A countdown section ships hidden and is revealed here. That is deliberate: unlike every
 * other dated string on these pages, a countdown has no correct server-rendered fallback,
 * because the number is only true for the second it was rendered. So it fails closed. If
 * this never runs, the reader sees no countdown rather than a wrong one, and the deadline
 * itself is still stated in plain text by the sections around it.
 *
 * @param {Element} section an element carrying data-cf-countdown
 * @param {string} nowIso
 */
export function paintCountdown(section, nowIso = new Date().toISOString()) {
  const rawKey = section.getAttribute('data-cf-countdown');
  const resolvedKey = rawKey === 'auto' ? autoEvent(nowIso) : rawKey;
  if (!resolvedKey) {
    section.hidden = true;
    return;
  }

  const parts = countdownParts(resolvedKey, nowIso);
  if (!parts) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  section.querySelectorAll('[data-cf-countdown-part]').forEach((el) => {
    const unit = el.getAttribute('data-cf-countdown-part');
    // Days is the only unit with no upper bound, so zero-padding it to two would lie at 100+.
    el.textContent = unit === 'days' ? String(parts[unit]) : String(parts[unit]).padStart(2, '0');
  });

  section.querySelectorAll('[data-cf-countdown-label]').forEach((el) => {
    const unit = el.getAttribute('data-cf-countdown-label');
    el.textContent = parts[unit] === 1 ? unit.slice(0, -1) : unit;
  });

  section.querySelectorAll('[data-cf-countdown-heading]').forEach((el) => {
    const heading = CF_EVENT_HEADINGS[resolvedKey];
    if (heading !== undefined) el.textContent = heading;
  });
}

/**
 * Start every countdown on the page and keep them ticking.
 * @returns {number|null} the interval id, or null when the page has no countdown
 */
export function startCountdowns(root = document) {
  const sections = Array.from(root.querySelectorAll('[data-cf-countdown]'));
  if (sections.length === 0) return null;

  const tick = () => sections.forEach((section) => paintCountdown(section));
  tick();
  return setInterval(tick, 1000);
}

function start() {
  // A failed render leaves the server-rendered dates in place, which are correct for
  // Pacific readers, and must not take the countdowns down with it.
  try {
    hydrate();
  } catch (error) {
    console.error('cf-time: could not localize dates', error);
  }
  startCountdowns();
  startZoneClock();
}

if (typeof document !== 'undefined') {
  // Several sections load this module, and one loaded late would miss DOMContentLoaded.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
